// One kept mock draft (MOCK_DRAFT.md §6.1, scenario C10): its grades and its
// board, read-only, for the manager who ran it or for any member once it is
// shared. Its manager can share it to the league's chat, once, or delete it.
//
// A PHONE SHOWS ONE AT A TIME, the grades or the board. A tablet stacks them,
// the board across the whole width, since beside the grades it had room for
// three seats of ten. From a desktop up they sit side by side, the grades the
// narrower column, since the board is the one that wants the width.

import { useState, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { MOCK_KEPT_DAYS, MOCKS_KEPT, type SavedMock } from '@fsp/domain';
import { messageOf } from '../data/api';
import { deviceZone, useLeagueBase, useReaderZone } from '../data/hooks';
import { useLeagueSource } from '../data/leagueSource';
import { deleteMock, shareMock, useSavedMock } from '../data/savedMocks';
import { EmptyState } from '../kit';
import { Panel } from '../kit/Panel';
import { Button, Chip, Dialog, Line, Segments, Stack, Text, tell } from '../kit/ui';
import { useTier } from '../shell/useTier';
import { MockGrades, gradesOfSaved } from './draft/MockGrades';
import { BoardPane } from './draft/panes';
import { DraftCards } from './draft/DraftCards';
import '../styles/screens/draft-room.css';

export function SavedMockScreen() {
  const source = useLeagueSource();
  if (source !== 'api') {
    return <EmptyState>Kept mocks are in your own leagues.</EmptyState>;
  }
  return <Kept />;
}

function Kept() {
  const { leagueId, mockId } = useParams();
  const base = useLeagueBase();
  // A MOCK JUST DELETED IS NOT ASKED FOR AGAIN: the delete asks every league
  // read again, and this one would be a refusal on the way out of the page.
  const [deleted, setDeleted] = useState(false);
  const { snapshot, reload } = useSavedMock(leagueId ?? null, deleted ? null : (mockId ?? null));
  if (snapshot.state !== 'ready' || snapshot.value === null) {
    const failed = snapshot.state === 'failed';
    const gone = failed && snapshot.failure.code === 'not_found';
    return (
      <div className="fsp-screen">
        <div className="fsp-widgets fsp-predraft-column">
          <Panel title="Mock draft">
            <Stack gap={2}>
              <Text tone="secondary" block>
                {!failed
                  ? 'Reading the mock…'
                  : gone
                    ? `This mock is not kept any more. A manager’s newest ${String(MOCKS_KEPT)} in a league are kept, each for ${String(MOCK_KEPT_DAYS)} days, and a manager can delete one.`
                    : snapshot.failure.message}
              </Text>
              {failed ? (
                <Line>
                  {gone ? null : <Button onClick={reload}>Try again</Button>}
                  <Button to={`${base}/draft/mock`} variant="quiet">
                    Mock draft
                  </Button>
                </Line>
              ) : null}
            </Stack>
          </Panel>
        </div>
      </div>
    );
  }
  return (
    <KeptMock
      mock={snapshot.value}
      leagueId={leagueId ?? ''}
      base={base}
      onDeleted={() => setDeleted(true)}
    />
  );
}

function KeptMock({
  mock,
  leagueId,
  base,
  onDeleted,
}: {
  mock: SavedMock;
  leagueId: string;
  base: string;
  onDeleted: () => void;
}) {
  const tier = useTier();
  const navigate = useNavigate();
  const zone = useReaderZone() ?? deviceZone();
  const [tab, setTab] = useState<'grades' | 'board'>('grades');
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const share = () => {
    if (busy) return;
    setBusy(true);
    void shareMock(leagueId, mock.id).then((reply) => {
      setBusy(false);
      if (reply.status === 'ok') tell('Shared to the league’s chat.', 'good');
      else tell(messageOf(reply, 'It could not be shared.'), 'bad');
    });
  };
  const remove = () => {
    if (busy) return;
    setBusy(true);
    void deleteMock(leagueId, mock.id).then((reply) => {
      setBusy(false);
      setConfirming(false);
      if (reply.status === 'ok') {
        onDeleted();
        tell('Mock deleted.');
        navigate(`${base}/draft/mock`, { replace: true });
      } else tell(messageOf(reply, 'It could not be deleted.'), 'bad');
    });
  };

  // A DAY, in my zone, and no zone's name on it: a date is not a moment.
  const when = new Date(mock.at).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: zone,
  });
  const how = `${mock.bots === 'instant' ? 'Instant' : 'Quick'} bots · ${mock.clock === 'none' ? 'no clock' : 'a clock'}`;
  const head = (
    <Panel title={mock.mine ? 'Your mock draft' : `${mock.owner}’s mock draft`}>
      <Stack gap={2}>
        <Text block>
          Pick {mock.slot} of {mock.teams}, {mock.rounds} rounds · {when}
        </Text>
        <Text size="xs" tone="secondary" block>
          {how}. {mock.mine ? 'Kept for you' : 'Shared with the league'}; nothing in it is the
          league’s real draft.
        </Text>
        <Line>
          {mock.mine ? (
            mock.sharedAt === null ? (
              <Button size="xs" loading={busy} onClick={share}>
                Share to league chat
              </Button>
            ) : (
              <Chip>Shared</Chip>
            )
          ) : null}
          <Button size="xs" variant="quiet" to={`${base}/draft/mock`}>
            {mock.mine ? 'Your mocks' : 'Mock draft'}
          </Button>
          {mock.mine ? (
            <Button size="xs" variant="danger-quiet" onClick={() => setConfirming(true)}>
              Delete
            </Button>
          ) : null}
        </Line>
      </Stack>
    </Panel>
  );
  const grades = (
    <MockGrades
      grades={gradesOfSaved(mock)}
      seats={mock.seats}
      owner={mock.mine ? null : mock.owner}
    />
  );
  const board = (
    <BoardPane
      settled
      auction={mock.type === 'auction'}
      view={{
        seats: mock.seats,
        rounds: mock.rounds,
        board: mock.board,
        current: null,
        // The manager's column marked, whoever is reading.
        myTeamId: mock.ownerTeamId,
      }}
    />
  );
  const dialog = (
    <Dialog opened={confirming} onClose={() => setConfirming(false)} title="Delete this mock?">
      <Stack gap={3}>
        <Text block>
          It goes from your mocks for good.
          {mock.sharedAt !== null
            ? ' Its card stays in the league’s chat, and opening it will say the mock is gone.'
            : ''}
        </Text>
        <Line>
          <Button variant="danger" loading={busy} onClick={remove}>
            Delete it
          </Button>
          <Button variant="quiet" onClick={() => setConfirming(false)}>
            Keep it
          </Button>
        </Line>
      </Stack>
    </Dialog>
  );

  let page: ReactNode;
  if (tier === 'phone') {
    // EACH COLUMN ONE GRID ITEM, at every width: panels side by side in
    // `.fsp-widgets` get rows sized from the container, not their content.
    page = (
      <div className="fsp-screen">
        <div className="fsp-widgets fsp-board-one">
          <div className="fsp-widget-column">
            {head}
            <Segments
              label="Mock"
              value={tab}
              onChange={(v) => setTab(v)}
              items={[
                { id: 'grades', label: 'Grades' },
                { id: 'board', label: 'Board' },
              ]}
            />
            <Panel title={tab === 'grades' ? 'Grades' : 'Board'}>
              {tab === 'grades' ? grades : board}
            </Panel>
            {/* Room under the last row for the floating chat button. */}
            <div className="fsp-list-chip-clearance" />
          </div>
        </div>
        {dialog}
      </div>
    );
  } else if (tier === 'tablet') {
    page = (
      <div className="fsp-screen">
        <div className="fsp-widgets fsp-board-one">
          <div className="fsp-widget-column">
            {head}
            <Panel title="Grades">{grades}</Panel>
            <Panel title="Board">{board}</Panel>
          </div>
        </div>
        {dialog}
      </div>
    );
  } else {
    page = (
      <div className="fsp-screen">
        <div className="fsp-widgets fsp-mock-kept">
          <div className="fsp-widget-column">
            {head}
            <Panel title="Grades">{grades}</Panel>
          </div>
          <div className="fsp-widget-column">
            <Panel title="Board">{board}</Panel>
          </div>
        </div>
        {dialog}
      </div>
    );
  }
  // A NAME OPENS THE DRAFT'S OWN CARD, which says where this mock took him,
  // and a seat its picks (B-337, B-338).
  return (
    <DraftCards seats={mock.seats} board={mock.board} available={[]} actions={null} kept>
      {page}
    </DraftCards>
  );
}
